import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { registerDevice, getRegistrationData, linkDevice } from '../services/api';
import storage from '../platform/storage';

/**
 * Registration data, device registration and account linking for
 * RegistrationForm.
 *
 * `loading` starts true: the form fetches the registration data on mount.
 *
 * @returns {{ registrationData, loading, error, fetchRegistrationData, register, link }}
 */
export const useRegistration = () => {
  const [error, setError] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch registration data for a language.
   * @param {string} language
   * @param {Function} [onLoaded] - called with the data once stored, before
   *   loading ends (the form uses it to pick defaults)
   */
  const fetchRegistrationData = async (language, onLoaded) => {
    try {
      setLoading(true);
      const data = await getRegistrationData(language);
      setRegistrationData(data);
      
      if (onLoaded) onLoaded(data);
      
      console.log('data=', data)
    } catch (error) {
      console.error('Failed to get registration data:', error);
      setError('Failed to load registration data');
    } finally {
      setLoading(false);
    }
  };

  // Get the stored device UID, generating one if not already stored
  const getDeviceUid = async () => {
    let deviceUid = await storage.getItem('deviceUid');
    if (!deviceUid) {
      deviceUid = uuidv4();
      await storage.setItem('deviceUid', deviceUid);
    }
    return deviceUid;
  };

  /**
   * Register this device as a new user.
   * @param {Object} form - countryCode, birthYear, gender, nickname,
   *   languageCode, colorPalette, isDarkMode, inviteCode
   * @param {Function} onRegistered - called after a successful registration
   */
  const register = async (form, onRegistered) => {
    const { countryCode, birthYear, gender, nickname, languageCode, colorPalette, isDarkMode, inviteCode } = form;
    setLoading(true);
    setError(null);

    try {
      const deviceUid = await getDeviceUid();

      const userData = {
        device_uid: deviceUid,
        country_code: countryCode,
        birth_year: birthYear ? parseInt(birthYear) : null,
        gender: gender || null,
        nickname: nickname,
        language_code: languageCode,
        color_palette: colorPalette,
        color_mode: isDarkMode ? 'dark' : 'light'
      };

      // Add invite code if present
      if (inviteCode) {
        userData.invited_by = inviteCode;
      }

      // Register the device
      const response = await registerDevice(userData);

      // Store the user_key from the response in storage
      if (response && response.user_key) {
        await storage.setItem('userKey', response.user_key);
      }

      // Clear invite code from storage after successful registration
      if (inviteCode) {
        await storage.removeItem('inviteCode');
      }

      // Notify parent component of successful registration
      onRegistered();
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError('This device is already registered. Please unregister first.');
      } else {
        setError('Registration failed. Please try again.');
        console.error('Registration error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Link this device to an existing account.
   * @param {Object} form - userKey, linkingCode
   * @param {Function} onRegistered - called after a successful link
   */
  const link = async ({ userKey, linkingCode }, onRegistered) => {
    setLoading(true);
    setError(null);

    try {
      const deviceUid = await getDeviceUid();

      const linkData = {
        user_key: userKey.trim(),
        device_uid: deviceUid,
        linking_code: linkingCode.trim()
      };

      // Link the device
      const response = await linkDevice(linkData);

      // Store the user_key in storage
      if (response && userKey) {
        await storage.setItem('userKey', userKey.trim());
      }

      // Notify parent component of successful linking
      onRegistered();
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Invalid user key or linking code. Please check and try again.');
      } else if (err.response && err.response.status === 409) {
        setError('This device is already linked to an account.');
      } else {
        setError('Account linking failed. Please try again.');
        console.error('Linking error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  return { registrationData, loading, error, fetchRegistrationData, register, link };
};

export default useRegistration;
